import { type entities, type enums, selectors } from "@fraction/shared";
import type { QueryClient } from "@tanstack/react-query";
import _ from "lodash";
import fraction, { type ChecklistApp } from "src/api/fraction";
import { updateApplicationInFetchCache } from "src/hooks/useApplication";
import type { PendingEntry } from "./applicationPendingChanges";
import { runCacheUpdater, runPostProcessor, SERIALIZERS } from "./pendingChangesSerializers";

/**
 * # Named save handlers for pending-changes entries.
 *
 * Each pending entry resolves to one saver name (default: `applicationFields`).
 * On Save, `PendingChangesSaveBar` groups entries by saver name and dispatches
 * each group through its registered handler. This lets fields whose storage
 * shape isn't a simple property-row column (e.g. property valuations — a
 * separate entity, not a column) participate in the same batched save UX as
 * normal fields without distorting the property PATCH endpoint.
 *
 * Add a new kind of editable field that needs a different write path? Add a
 * key here and tag the relevant entries with `saver: { name, args }`. The
 * `args` payload is JSON-serializable per-entry config (entity ids, enum
 * discriminators) — savers must avoid relying on closures so the store entry
 * stays inspectable / GC-friendly.
 */

export type SaverContext = {
  applicationId: string;
  app: ChecklistApp | undefined;
  queryClient: QueryClient;
  /** The default saver delegates to this — the existing batched-PATCH path.
   *  Resolves only after all underlying PATCH mutations settle. */
  saveFields: (
    changes: Record<string, any>,
    opts?: {
      cacheUpdateFnProperty?: (
        prev: entities.Property | undefined | null,
        mergedChanges: entities.Property
      ) => entities.Property | null | undefined;
    }
  ) => Promise<void>;
};

type SaverFn = (entries: PendingEntry[], ctx: SaverContext) => void | Promise<void>;

export const SAVERS = {
  /**
   * Default: fold entries into a single `changes` map and route through the
   * existing `saveFields` mutation (application/property/loan PATCH). Composes
   * `cacheUpdate` named updaters into one optimistic property updater.
   */
  applicationFields: (async (entries, ctx) => {
    const changes: Record<string, any> = {};
    for (const e of entries) {
      const storageValue = SERIALIZERS[e.serializer].toStorage(e.rawValue);
      const finalValue = e.postProcess
        ? runPostProcessor(e.postProcess.name, storageValue, e.postProcess.args)
        : storageValue;
      changes[e.path] = finalValue;
    }
    if (Object.keys(changes).length === 0) return;

    const cacheUpdaters = entries.map((e) => e.cacheUpdate).filter((u): u is NonNullable<typeof u> => !!u);
    const composedCacheUpdateFnProperty = cacheUpdaters.length
      ? (prev: entities.Property | undefined | null, mergedChanges: entities.Property) => {
          let acc: entities.Property | null | undefined = prev;
          for (const u of cacheUpdaters) {
            acc = runCacheUpdater(u.name, acc, mergedChanges, u.args);
          }
          return acc;
        }
      : undefined;

    await ctx.saveFields(changes, { cacheUpdateFnProperty: composedCacheUpdateFnProperty });
  }) satisfies SaverFn,

  /**
   * Property valuations — child entity of Property, not a column. For each
   * entry, find the most recent matching `(type, source)` row by
   * `date || createdAt` and PATCH it; otherwise POST a new row. Optimistic
   * cache update is applied across all `*ApplicationKey` cache slices.
   *
   * Per-entry `args`: `{ type: PropertyValuationType; source: PropertyValuationSource }`.
   */
  propertyValuation: (async (entries, ctx) => {
    const propertyId = ctx.app?.property?.id;
    if (!propertyId) return;
    const valuations = ctx.app?.property?.valuations ?? [];

    const ops = entries
      .map((e) => {
        const cents = SERIALIZERS[e.serializer].toStorage(e.rawValue);
        if (typeof cents !== "number") return null;
        const args = (e.saver?.args ?? {}) as {
          type: enums.PropertyValuationType;
          source: enums.PropertyValuationSource;
        };
        if (!args.type || !args.source) return null;
        const matching = _.maxBy(
          valuations.filter((v) => v.type === args.type && v.source === args.source),
          (v) => +new Date(v?.date ?? v?.createdAt ?? 0)
        );
        if (matching?.value === cents) return null;
        return { cents, matching, args };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    if (ops.length === 0) return;

    const date = new Date();

    updateApplicationInFetchCache(ctx.queryClient, ctx.applicationId, (prev) => {
      if (!prev?.property) return prev;
      let next = [...(prev.property.valuations ?? [])];
      for (const op of ops) {
        if (op.matching?.id) {
          const id = op.matching.id;
          next = next.map((v) => (v.id === id ? { ...v, value: op.cents, date } : v));
        } else {
          next = [
            ...next,
            {
              value: op.cents,
              date,
              type: op.args.type,
              source: op.args.source,
            } as entities.PropertyValuation,
          ];
        }
      }
      return { ...prev, property: { ...prev.property, valuations: next } };
    });

    await Promise.all(
      ops.map((op) =>
        op.matching?.id
          ? fraction.updatePropertyValuation(ctx.applicationId, propertyId, op.matching.id, {
              value: op.cents,
              date,
            })
          : fraction.createPropertyValuation(ctx.applicationId, propertyId, {
              value: op.cents,
              date,
              type: op.args.type,
              source: op.args.source,
            })
      )
    );
  }) satisfies SaverFn,

  /**
   * Applicant / property / income / debt / expense / asset rows — child entities, each with
   * its own PATCH endpoint, so they can't ride the application/property PATCH
   * the default saver uses.
   *
   * Entries are grouped by `(kind, id)` so several edits to the same row (a
   * debt's balance and its payout flag, say) collapse into one request. The
   * column is the last segment of the path, which keeps the store key unique
   * per field — `debt.<id>.currentlyOwing`.
   *
   * Per-entry `args`: `{ kind: RowKind; id: string }`.
   */
  applicationEntityRow: (async (entries, ctx) => {
    type RowKind = "applicant" | "property" | "income" | "debt" | "expense" | "asset";

    const grouped = new Map<string, { kind: RowKind; id: string; updates: Record<string, any> }>();

    for (const entry of entries) {
      const args = (entry.saver?.args ?? {}) as { kind?: RowKind; id?: string };
      if (!args.kind || !args.id) {
        continue;
      }
      const column = entry.path.split(".").pop();
      if (!column) {
        continue;
      }
      const storageValue = SERIALIZERS[entry.serializer].toStorage(entry.rawValue);
      const value = entry.postProcess
        ? runPostProcessor(entry.postProcess.name, storageValue, entry.postProcess.args)
        : storageValue;

      const key = `${args.kind}:${args.id}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.updates[column] = value;
      } else {
        grouped.set(key, { kind: args.kind, id: args.id, updates: { [column]: value } });
      }
    }

    if (!grouped.size) {
      return;
    }

    // Optimistic pass over every place these rows are reachable in the cached
    // application graph. There is no top-level `application.debts` — rows hang
    // off applicants and off the property (the selectors read both), so a
    // top-level merge would find nothing and the edit would appear to revert
    // until the refetch landed.
    updateApplicationInFetchCache(ctx.queryClient, ctx.applicationId, (prev) => {
      if (!prev) {
        return prev;
      }
      const patch = <T extends { id?: string }>(rows: T[] | undefined, kind: RowKind): T[] | undefined =>
        rows?.map((row) => {
          const match = row.id ? grouped.get(`${kind}:${row.id}`) : undefined;
          return match ? ({ ...row, ...match.updates } as T) : row;
        });

      const next = {
        ...prev,
        applicants: prev.applicants?.map((applicant) => ({
          ...applicant,
          ...(grouped.get(`applicant:${applicant.id}`)?.updates ?? {}),
          assets: patch(applicant.assets, "asset"),
          properties: patch(applicant.properties, "property"),
          incomes: patch(applicant.incomes, "income"),
          debts: patch(applicant.debts, "debt"),
          mergedDebts: patch(applicant.mergedDebts, "debt"),
          expenses: patch(applicant.expenses, "expense"),
        })),
        property: prev.property
          ? {
              ...prev.property,
              ...(grouped.get(`property:${prev.property.id}`)?.updates ?? {}),
              incomes: patch(prev.property.incomes, "income"),
              debts: patch(prev.property.debts, "debt"),
              mergedDebts: patch(prev.property.mergedDebts, "debt"),
              expenses: patch(prev.property.expenses, "expense"),
            }
          : prev.property,
      };
      return selectors.incomes.mapApplicationIncomeRows(next, (income) => {
        const updates = income.id ? grouped.get(`income:${income.id}`)?.updates : undefined;
        return updates ? { ...income, ...updates } : income;
      });
    });

    const originalIncomes = ctx.app ? selectors.application.selectAllIncomes(ctx.app) : [];
    const results = await Promise.allSettled(
      [...grouped.values()].map(({ kind, id, updates }) => {
        if (kind === "income") {
          return fraction.updateIncome(ctx.applicationId, id, updates).catch((error: unknown) => {
            if ("excludeFromApplication" in updates) {
              const original = originalIncomes.find((income) => income.id === id);
              updateApplicationInFetchCache(ctx.queryClient, ctx.applicationId, (app) =>
                app
                  ? selectors.incomes.mapApplicationIncomeRows(app, (income) =>
                      income.id === id && income.excludeFromApplication === updates.excludeFromApplication
                        ? { ...income, excludeFromApplication: original?.excludeFromApplication }
                        : income
                    )
                  : app
              );
            }
            throw error;
          });
        }
        if (kind === "debt") {
          return fraction.updateDebt(ctx.applicationId, id, updates);
        }
        if (kind === "expense") {
          return fraction.updateExpense(ctx.applicationId, id, updates);
        }
        if (kind === "asset") {
          return fraction.updateAsset(ctx.applicationId, id, updates);
        }
        if (kind === "property") {
          return fraction.updateProperty(ctx.applicationId, id, updates);
        }
        return fraction.updateApplicant(ctx.applicationId, id, updates);
      })
    );
    const failure = results.find((result) => result.status === "rejected");
    if (failure?.status === "rejected") throw failure.reason;
  }) satisfies SaverFn,

  /**
   * Insurance policy fields — the policy is its own entity with its own endpoint. Entries are
   * grouped per policy so one Save sends one request per policy. A file with no policy row yet
   * (`policyId` unset) creates it on save, linked to `fileId`.
   *
   * Per-entry `args`: `{ policyId?: string; fileId?: string }`. The column is the last path segment.
   */
  insurancePolicy: (async (entries, ctx) => {
    const grouped = new Map<
      string,
      { policyId?: string; fileId?: string; updates: Partial<entities.InsurancePolicy> }
    >();
    for (const entry of entries) {
      const args = (entry.saver?.args ?? {}) as { policyId?: string; fileId?: string };
      const column = entry.path.split(".").pop();
      if (!column || (!args.policyId && !args.fileId)) {
        continue;
      }
      const key = args.policyId ?? `file:${args.fileId}`;
      const group = grouped.get(key) ?? { policyId: args.policyId, fileId: args.fileId, updates: {} };
      Object.assign(group.updates, { [column]: SERIALIZERS[entry.serializer].toStorage(entry.rawValue) });
      grouped.set(key, group);
    }

    await Promise.all(
      [...grouped.values()].map(async ({ policyId, fileId, updates }) => {
        const saved = policyId
          ? await fraction.updateInsurancePolicy(ctx.applicationId, policyId, undefined, updates)
          : fileId
            ? await fraction.createInsurancePolicy(ctx.applicationId, fileId, updates)
            : undefined;
        if (!saved) {
          return;
        }
        // The save bar refetches the application graph (files carry their policy), but each policy
        // card reads its own query.
        ctx.queryClient.setQueryData(
          ["insurancePolicy", ctx.applicationId, saved.id],
          (prev: entities.InsurancePolicy | null | undefined) => ({ ...(prev || {}), ...saved })
        );
      })
    );
  }) satisfies SaverFn,
} as const;

export type SaverName = keyof typeof SAVERS;

export const runSaver = (
  name: SaverName,
  entries: PendingEntry[],
  ctx: SaverContext
): void | Promise<void> => {
  return (SAVERS[name] as SaverFn)(entries, ctx);
};
